/**
 * CampaignInputCardElement
 */

import { OmniElement, OmniStyleElement, html, css, nothing } from 'omni-ui';

OmniStyleElement.register();
/**
 * @summary customizable input card
 * @description custom component styled as a toggle button and card, used for collecting user input/data. A render template can be passed to it or slotted childern depending on the developers needs
 * @extends OmniElement
 */
export class CampaignInputCard extends OmniElement {
  /**
   * CampaignInputCard Properties
   * @property {string} headerText string that is displayed as the header of the component
   * @property {Boolean} cardHidden is card hidden or displayed flag
   * @property {string} confirmBtnText string that is displayed as the confirm button's label ex. "add", "submit" etc.
   * @property {string} alignCard can be passed the string "right" or "center" to right/center align the card to the toggle button. The defualt is left align
   * @property {string} toggleBtnText string that can be used as the label for the toggle button
   * @property {function} handleSubmit property to pass down a function for data handling and submit
   * @property {string} error message from any exception thrown during submit to display (reset on close)
   * @property {string} openBtnIcon icon string that can be optionally passed in; defaulted to omni:interactive:add
   * @property {template} bodyTemplate a render function for the card body
   * @property {template} invokerTemplate a render function for the card invoker
   * @property {template} footerTemplate a render function for the card footer
   */
  static get properties() {
    return {
      headerText: { type: String },
      cardHidden: { type: Boolean },
      confirmBtnText: { type: String },
      alignCard: { type: String },
      toggleBtnText: { type: String },
      handleSubmit: { attribute: false },
      error: { type: String },
      openBtnIcon: { type: String },
      tooltipText: { type: String },
      bodyTemplate: { attribute: false },
      invokerTemplate: { attribute: false },
      footerTemplate: { attribute: false },
    };
  }

  /**
   * CampaignInputCard css styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        .omni .parent-container {
          position: relative;
          display: inline-block;
        }
        .omni .module-container {
          position: absolute;
          z-index: 5;
          top: 43px;
          display: flex;
          flex-flow: column nowrap;
          width: 355px;
          height: fit-content;
          padding: 0px;
        }

        .align-right {
          right: 0;
        }

        .align-center {
          right: -153px;
        }

        .omni .open-btn {
          min-width: 44px;
          height: 36px;
        }

        .omni header {
          display: flex;
          width: 100%;
          height: 42px;
          border-bottom: 1px solid rgb(var(--rgb-border));
          justify-content: flex-start;
          align-items: center;
          padding: 10px 16px;
        }

        .omni header span.header-text {
          opacity: 0.9;
          font-size: 14px;
          line-height: 1;
          color: rgb(var(--rgb-core-dark));
        }

        .omni form {
          display: flex;
          flex-flow: column nowrap;
          margin: 24px 24px 0 24px;
        }

        .omni form .error {
          color: red;
          font-size: 12px;
        }

        .omni footer.footer-btns {
          display: flex;
          justify-content: flex-end;
          margin: 0px 24px 24px 24px;
        }

        .omni footer button.button.is-text {
          margin-right: 10px;
        }
        .invoker-tooltip {
          margin: 0 1.5rem 1.5rem 0;
        }
      `,
    ];
  }

  /**
   *Constructor to initialize {@link #confirmBtnText}, {@link #alignCard}, and {@link #cardHidden}
   */
  constructor() {
    super();
    this.confirmBtnText = '';
    this.alignCard = '';
    this.cardHidden = true;
  }

  /**
   * Initialize event listener 'click' to invoke {@link ##closeInputCard}
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#closeInputCard);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#closeInputCard);
    super.disconnectedCallback();
  }

  /**
   *
   * @param {*} event passed by the event listener "click"
   * @description mentod that handles the closing of the component
   * @returns out of the method
   */
  #closeInputCard = async event => {
    const inputCard = this.shadowRoot.querySelector('.parent-container');
    if (this.cardHidden || event.composedPath().includes(inputCard)) {
      return;
    }

    this.cardHidden = true;
    this.error = '';
    await this.updateCompleted;
    this.dispatchNewEvent('campaign-input-card-close');
  };

  /**
   * @description method to toggle the component open or close
   */
  #toggleInputCard = async () => {
    this.cardHidden = !this.cardHidden;
    this.error = '';
    await this.updateCompleted;
    this.dispatchNewEvent(
      `campaign-input-card-${this.cardHidden ? 'close' : 'open'}`,
    );
  };

  /**
   *
   * @param {*} e passed event
   * @description Uses is-loading to indicate the submit is in progress. Also use it to signal that we can ignore further clicking while we are still processing. The handleSubmit() function can be asynchronous and this method will await for it before closing the input card. Any exceptions will cause the card to stay open and the exception error message will be displayed at the top of the card.
   * @returns out of the method
   */
  #doSubmit = async e => {
    e.preventDefault();
    if (e.target.classList.contains('is-loading')) return;
    try {
      this.error = '';
      e.target.classList.add('is-loading');
      await this.handleSubmit(this);
      this.#toggleInputCard();
    } catch (ex) {
      this.error = ex.message;
    } finally {
      e.target.classList.remove('is-loading');
    }
  };

  /**
   * @description main render method that returns the components html template literal. Split between to main sections. The toggle button that is always visible and then the card the conditionally renders depending on the state of {@link #cardHidden}
   * @returns main html template for the component
   */
  render() {
    return html`
      <omni-style>
        <div part="parent-container" class="parent-container">
          <div part="invoker">
            ${this.invokerTemplate
              ? this.invokerTemplate(this.#toggleInputCard)
              : html` <omni-tooltip
                  part="tooltip"
                  class="invoker-tooltip ${this.readonly ? 'is-hidden' : ''}"
                >
                  <div slot="content">${this.tooltipText}</div>
                  <button
                    part="toggle-open-btn"
                    class="button is-outlined open-btn"
                    id="viz4-btn"
                    slot="invoker"
                    @click=${this.#toggleInputCard}
                  >
                    <omni-icon
                      part="toggle-open-btn-icon"
                      class="is-size-1"
                      icon-id="${this.openBtnIcon ?? 'omni:interactive:add'}"
                    ></omni-icon>
                    ${this.toggleBtnText
                      ? html`<span part="toggle-btn-text"
                          >${this.toggleBtnText}</span
                        >`
                      : ''}
                  </button>
                </omni-tooltip>`}
          </div>
          ${this.cardHidden
            ? null
            : html`
                <div
                  part="module-container"
                  class="box module-container ${this.alignCard === 'right'
                    ? 'align-right'
                    : ''}
                    ${this.alignCard === 'center' ? 'align-center' : ''}"
                >
                  <header part="header">
                    <span class="header-text">${this.headerText}</span>
                  </header>
                  <form
                    part="main"
                    class="card-inputs"
                    @submit=${this.#doSubmit}
                  >
                    ${this.error
                      ? html`<div class="error mb-5">${this.error}</div>`
                      : ''}
                    ${this.bodyTemplate
                      ? this.bodyTemplate(this.#toggleInputCard)
                      : html`<slot></slot>`}

                    <footer part="footer-btns" class="footer-btns">
                      ${this.footerTemplate
                        ? this.footerTemplate(this.#toggleInputCard)
                        : html`
                            <button
                              part="cancel-btn"
                              class="button is-text"
                              @click="${this.#toggleInputCard}"
                              type="button"
                            >
                              Cancel
                            </button>
                            ${this.confirmBtnText && this.handleSubmit
                              ? html` <button
                                  part="confirmation-btn"
                                  class="button is-primary"
                                  type="submit"
                                >
                                  ${this.confirmBtnText}
                                </button>`
                              : nothing}
                          `}
                    </footer>
                  </form>
                </div>
              `}
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-input-card', CampaignInputCard);
